import { installKeyboard, onKey, consumePauseToggle } from './core/input';
import { startLoop } from './core/loop';
import { World } from './game/world';
import { Renderer } from './game/render';
import { Campaign, STARTING_STATS, cloneCarried } from './game/campaign';
import { LEVEL_COUNT } from './game/levels';
import { loadSave, saveSave } from './core/save';
import { AudioBus } from './core/audio';

/**
 * Bootstrap для кампанії. Менеджер сцен керує переходами:
 *   menu → playing → paused / won → next playing | gameover → menu
 */
function bootstrap(): void {
  const canvas = document.getElementById('game') as HTMLCanvasElement | null;
  const fpsEl  = document.getElementById('fps');
  const menuMaybe = document.getElementById('menu') as HTMLDivElement | null;
  const stageBanner = document.getElementById('stage-banner');
  if (!canvas || !menuMaybe) throw new Error('DOM elements missing');
  const menuEl: HTMLDivElement = menuMaybe;

  installKeyboard();
  const renderer = new Renderer(canvas);
  const audio = new AudioBus(loadSave().settings.audio);

  let campaign: Campaign | null = null;
  let world: World | null = null;
  let bannerTimer = 0;
  let paused = false;

  // ───────────── Menu rendering ─────────────

  function showMenu() {
    paused = false;
    const save = loadSave();
    menuEl.style.display = 'flex';
    menuEl.innerHTML = `
      <div class="menu-card">
        <h2>BOMBERMAN WEB</h2>
        <div class="menu-stats">
          <div>High Score: <b>${save.highScore}</b></div>
          <div>Best Run: <b>${save.fastestRunMs ? formatMs(save.fastestRunMs) : '—'}</b></div>
          <div>Unlocked: <b>${save.unlockedLevel} / ${LEVEL_COUNT}</b></div>
        </div>
        <button data-act="new">New Game</button>
        <button data-act="continue" ${save.unlockedLevel <= 1 ? 'disabled' : ''}>
          Continue (Level ${save.unlockedLevel})
        </button>
        <button data-act="options">Options</button>
        <button data-act="reset">Reset Save</button>
        <p class="menu-hint">WASD/стрілки · Space бомба · Q детонатор · Esc/P пауза · R рестарт</p>
      </div>
    `;
    bindMenu({ new: () => startCampaign(1),
               continue: () => startCampaign(save.unlockedLevel),
               options: () => showOptions('menu'),
               reset:   () => { try { localStorage?.removeItem('bbm:save'); } catch { /* */ } showMenu(); } });
  }

  function showOptions(returnTo: 'menu' | 'pause') {
    const save = loadSave();
    const a = save.settings.audio;
    menuEl.style.display = 'flex';
    menuEl.innerHTML = `
      <div class="menu-card">
        <h2>OPTIONS</h2>
        <label class="opt-row">
          <span>Sound</span>
          <input type="checkbox" id="opt-sound" ${a.enabled ? 'checked' : ''}>
        </label>
        <label class="opt-row">
          <span>Volume</span>
          <input type="range" id="opt-vol" min="0" max="100" value="${Math.round(a.volume * 100)}">
        </label>
        <button data-act="back">Back</button>
      </div>
    `;
    const sound = menuEl.querySelector<HTMLInputElement>('#opt-sound')!;
    const vol   = menuEl.querySelector<HTMLInputElement>('#opt-vol')!;
    const persist = () => {
      const s = loadSave();
      s.settings.audio = { enabled: sound.checked, volume: +vol.value / 100 };
      saveSave(s);
      audio.setPrefs(s.settings.audio);
    };
    sound.addEventListener('change', () => { persist(); audio.play('menu_select'); });
    vol.addEventListener('input', persist);
    bindMenu({ back: () => returnTo === 'menu' ? showMenu() : showPauseMenu() });
  }

  function showPauseMenu() {
    paused = true;
    menuEl.style.display = 'flex';
    menuEl.innerHTML = `
      <div class="menu-card">
        <h2>PAUSED</h2>
        <button data-act="resume">Resume</button>
        <button data-act="restart">Restart Level</button>
        <button data-act="options">Options</button>
        <button data-act="quit">Quit to Menu</button>
      </div>
    `;
    bindMenu({
      resume:  () => { paused = false; menuEl.style.display = 'none'; audio.play('menu_select'); },
      restart: () => { paused = false; menuEl.style.display = 'none'; startLevel(); },
      options: () => showOptions('pause'),
      quit:    () => { campaign = null; world = null; showMenu(); },
    });
  }

  function bindMenu(handlers: Record<string, () => void>) {
    menuEl.querySelectorAll<HTMLButtonElement>('button').forEach(btn => {
      btn.addEventListener('click', () => {
        const act = btn.dataset.act ?? '';
        audio.ensureContext();
        audio.play('menu_select');
        handlers[act]?.();
      });
    });
  }

  function startCampaign(level: number) {
    audio.ensureContext();
    campaign = new Campaign(level);
    menuEl.style.display = 'none';
    startLevel();
  }

  function startLevel() {
    if (!campaign) return;
    world = new World(campaign.config(), campaign.carried, {
      onBombPlace:  () => audio.play('bomb_place'),
      onExplosion:  () => audio.play('explosion'),
      onPickup:     () => audio.play('pickup'),
      onEnemyDie:   () => audio.play('enemy_die'),
      onPlayerDie:  () => audio.play('player_die'),
      onStageClear: () => audio.play('stage_clear'),
    });
    bannerTimer = 90;
    if (stageBanner) {
      stageBanner.textContent = `Stage ${campaign.current}${world.config.bonus ? ' — BONUS' : ''}`;
      stageBanner.style.opacity = '1';
    }
  }

  onKey('KeyR', () => { if (campaign && world && !paused) startLevel(); });

  startLoop({
    tick: () => {
      // Pause toggle працює лише під час гри
      if (consumePauseToggle()) {
        if (world && !paused) showPauseMenu(), audio.play('pause');
        else if (paused) {
          paused = false;
          menuEl.style.display = 'none';
          audio.play('pause');
        }
      }
      if (paused || !world || !campaign) return;
      world.tick();
      if (bannerTimer > 0 && --bannerTimer === 0 && stageBanner) {
        stageBanner.style.opacity = '0';
      }

      if (world.status === 'won') {
        const carried = world.toCarried();
        const advanced = campaign.advance(carried);
        if (advanced) {
          startLevel();
        } else {
          campaign.gameOver(carried.score);
          alert(`Перемога! Фінальний рахунок: ${carried.score}`);
          campaign = null;
          world = null;
          showMenu();
        }
      } else if (world.status === 'gameover') {
        const finalScore = world.toCarried().score;
        if (campaign) campaign.gameOver(finalScore);
        setTimeout(() => {
          if (campaign === null) return;
          campaign = null;
          world = null;
          showMenu();
        }, 2000);
      }
    },
    render: () => {
      if (world) renderer.draw(world);
    },
    onFps: (fps) => { if (fpsEl) fpsEl.textContent = `${fps} fps`; },
  });

  showMenu();
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

bootstrap();
void STARTING_STATS;
void cloneCarried;
